/**
 * 발주서 API 클라이언트 — backend /purchase-orders/* 와 1:1.
 */

import type { ApiResponse } from './types';

const BASE = '/api';

export type PoCompany = 'SM' | 'EJ';
export type PoType = 'standard' | 'quote_attached';
/**
 * 목록·상세에 오는 종류 — 작성·수정 폼이 고를 수 있는 PoType 에 「📁 옛 발주서(원본 PDF · v2.291.0)」를 더한 것.
 * 옛 발주서는 포털 이전에 보낸 PDF 를 그대로 올린 것이라 고치기·복사·[매입 연동]이 없다(서버도 막는다).
 */
export type PoKind = PoType | 'legacy_pdf';

export interface PoContactDefaults {
  empId: string;
  name: string;
  title: string;
  displayName: string;
  phone: string;
  email: string;
}

export interface PoItemInput {
  itemName: string;
  description: string;
  qty: number;
  unitPrice: number;
  note: string;
}

export interface PoCreateInput {
  companyId: PoCompany;
  poDate: string;
  poType?: PoType;
  /** 견적서 첨부형 발주 총액(VAT 포함, 선택) */
  totalAmount?: number;
  recipientSupplier: string;
  reference: string;
  projectName: string;
  deliveryPlace: string;
  deliveryDue: string;
  paymentTerm: string;
  linkedSalesId?: string | null;
  /** 관련 영업건 여러 개(v2.284.0 · 개발요청 #5) — 첫 번째 = 대표. 보내면 linkedSalesId 보다 먼저 */
  linkedSalesIds?: string[];
  remark: string;
  empName?: string;
  empTitle?: string;
  contactPhone?: string;
  contactEmail?: string;
  items: PoItemInput[];
}

export interface PoUpdateInput {
  companyId?: PoCompany;
  poDate?: string;
  poType?: PoType;
  totalAmount?: number;
  recipientSupplier?: string;
  reference?: string;
  projectName?: string;
  deliveryPlace?: string;
  deliveryDue?: string;
  paymentTerm?: string;
  linkedSalesId?: string | null;
  linkedSalesIds?: string[];
  remark?: string;
  empName?: string;
  empTitle?: string;
  contactPhone?: string;
  contactEmail?: string;
  items?: PoItemInput[];
}

export interface PoListRow {
  id: number;
  poNo: string;
  companyId: PoCompany;
  poDate: string;
  poType: PoKind;
  empId: string | null;
  empName: string;
  empTitle: string;
  empDisplay: string;
  contactPhone: string;
  contactEmail: string;
  recipientSupplier: string;
  reference: string;
  projectName: string;
  deliveryPlace: string;
  deliveryDue: string;
  paymentTerm: string;
  /** 대표 영업건 */
  linkedSalesId: string | null;
  /** 관련 영업건 전부 — 대표 먼저 (v2.284.0) */
  linkedSalesIds: string[];
  /** [매입 연동]으로 만든 매입 줄 수 (v2.284.0) */
  linkedPurchaseCount: number;
  totalSupply: number;
  totalWithVat: number;
  firstItemName: string;
  firstItemDescription: string;
  itemCount: number;
  createdBy: string;
  createdAt: string;
}

/** 발주서 미리보기 아래 「이 발주서로 등록된 매입」 한 줄 */
export interface PoLinkedPurchase {
  purchaseId: number;
  poSortOrder: number | null;
  /** 연동 · 상속 · 수동 · 정리 */
  how: string;
  itemName: string | null;
  description: string | null;
  orderQty: number;
  salesId: string | null;
  salesEndUser: string | null;
  actualInDate: string | null;
  actualOutDate: string | null;
  parentPurchaseId: number | null;
}

export interface PoDetail extends PoListRow {
  remark: string;
  updatedAt: string;
  /** 관련 영업건 이름·판보 확정 여부 — 대표 먼저 (v2.284.0) */
  linkedSales: { salesId: string; projectName: string | null; endUser: string | null; reportFinalized: boolean }[];
  /** 이 발주서로 이미 [매입 연동]했나 */
  linkStatus: { purchaseCount: number; lastLinkedAt: string | null };
  /** 이 발주서로 등록된 매입 — 나뉜 줄(상속)·손으로 이은 줄(수동)·10-21 정리 포함 (v2.287.0) */
  linkedPurchases: PoLinkedPurchase[];
  items: Array<{
    id: number;
    sortOrder: number;
    itemName: string;
    description: string;
    qty: number;
    unitPrice: number;
    lineTotal: number;
    note: string;
  }>;
}

export interface PoCreateResult {
  id: number;
  poNo: string;
  totalSupply: number;
  totalWithVat: number;
}

/**
 * 새 발주서 작성 폼에 미리 채울 내용 — 「이 내용으로 새 발주서」(v2.213.0 · 견적서 복사와 같은 개념).
 * 발주일자·발주번호·관련 영업건·첨부 파일은 **들어 있지 않다** (아래 poDetailToDraft 참고).
 */
export interface PoDraft {
  companyId: PoCompany;
  poType: PoType;
  /** 견적서 첨부형 총액(VAT 포함) — 품목 직접 입력형은 0 */
  totalAmount: number;
  recipientSupplier: string;
  reference: string;
  projectName: string;
  deliveryPlace: string;
  deliveryDue: string;
  paymentTerm: string;
  remark: string;
  items: PoItemInput[];
  /** 원본 발주번호 — 폼 제목·안내 문구용 */
  sourcePoNo: string;
  /** 원본의 담당 한 줄(이름·직책) — 폼 안내 문구용(**새 발주서의 담당은 쓰는 사람**) */
  sourceEmpName: string;
  /** 원본의 담당 사번 — 「다른 사람의 발주서를 복사했나」 판정용(이름 글자는 직책이 바뀌면 달라진다) */
  sourceEmpId: string | null;
  /** 원본의 관련 영업건 — 폼이 [같은 영업건으로] 제안에만 쓴다(**자동으로 넣지 않는다**) */
  sourceLinkedSalesId: string | null;
  /** 원본 발주서의 관련 영업건 전부(v2.284.0) — [같은 영업건으로] 가 한 번에 넣는다 */
  sourceLinkedSalesIds?: string[];
}

/**
 * 저장된 발주서 → 새 발주서 작성 초안 (v2.213.0 · 사용자 요청 2026-09-28
 * 「발주서 작성할 때도 기존 발주서에서 이 내용으로 새 발주서 작성 기능」).
 *
 * 내용은 그대로 가져오되:
 *   · **발주일자는 넣지 않는다** → 작성 폼이 오늘로 채운다(발주번호는 저장 때 새로 매겨진다) — 견적서 복사와 같다.
 *   · **관련 영업건은 넣지 않는다** — 원본 번호는 sourceLinkedSalesId 로만 넘겨 [같은 영업건으로] 한 번에 넣게 한다.
 *     영업건은 발주서 PDF 의 NAS 보관 폴더와 [매입 연동]이 만드는 매입의 영업건을 정한다 → 모르고 따라가면
 *     **엉뚱한 건에 붙는다.** 운영 실측(09-28): 비슷한 발주서를 다시 쓴 3묶음 중 같은 영업건 1 · 다른 영업건 2.
 *   · **결제조건은 원본 그대로** — 손으로 고친 조건이 많다(운영 28장 중 13장이 등록값·기본값이 아님).
 *     폼은 매입처를 **바꿀 때까지** 다시 채우지 않는다.
 *   · 거래처 견적서 첨부 파일은 원본 발주서의 것이라 복사하지 않는다(폼이 안내한다).
 *   · **담당·연락처·이메일은 넣지 않는다**(v2.213.2) → 폼이 쓰는 사람(로그인한 사람)으로 채운다.
 *     운영 발주서 28장 모두 담당 = 쓴 사람(09-28 실측 · 권애영 21 · 황재상 5 · 김상엽 2) — 원본 담당을 그대로 두면
 *     다른 사람이 복사했을 때 **엉뚱한 사람 이름·전화가 매입처로 나간다.** 원본 담당은 sourceEmpName 으로 안내에만.
 */
export function poDetailToDraft(detail: PoDetail): PoDraft {
  const isQuote = detail.poType === 'quote_attached';
  return {
    companyId: detail.companyId,
    poType: isQuote ? 'quote_attached' : 'standard',
    totalAmount: isQuote ? Number(detail.totalWithVat) || 0 : 0,
    recipientSupplier: detail.recipientSupplier,
    reference: detail.reference,
    projectName: detail.projectName,
    deliveryPlace: detail.deliveryPlace,
    deliveryDue: detail.deliveryDue,
    paymentTerm: detail.paymentTerm,
    remark: detail.remark ?? '',
    items: isQuote
      ? []
      : detail.items.map((it) => ({
          itemName: it.itemName,
          description: it.description,
          qty: it.qty,
          unitPrice: it.unitPrice,
          note: it.note,
        })),
    sourcePoNo: detail.poNo,
    sourceLinkedSalesId: detail.linkedSalesId ?? null,
    sourceLinkedSalesIds: detail.linkedSalesIds ?? (detail.linkedSalesId ? [detail.linkedSalesId] : []),
    sourceEmpName: detail.empDisplay ?? '',
    sourceEmpId: detail.empId ?? null,
  };
}

async function unwrap<T>(r: Response): Promise<T> {
  const json = (await r.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function fetchPoContactDefaults(): Promise<PoContactDefaults> {
  const r = await fetch(`${BASE}/purchase-orders/contact-defaults`, { credentials: 'include' });
  return unwrap<PoContactDefaults>(r);
}

export async function fetchPoSuppliers(): Promise<string[]> {
  const r = await fetch(`${BASE}/purchase-orders/lookup/suppliers`, { credentials: 'include' });
  const data = await unwrap<{ items: string[] }>(r);
  return data.items;
}

/** 발주서 품목 검색 후보 (재고관리 '품명&품목' = item_master 기반) */
export interface PoLookupItem {
  itemCode: string;
  itemGroup: string;
  itemName: string;
  spec: string | null;
}

/**
 * 품목 후보 조회.
 *   supplier 지정 시 그 매입처에서 과거 매입한 품목만 (최근 매입 우선),
 *   미지정 시 전체 품목.
 */
export async function fetchPoItems(
  supplier?: string,
): Promise<{ supplierFiltered: boolean; items: PoLookupItem[] }> {
  const qs = new URLSearchParams();
  const s = (supplier ?? '').trim();
  if (s) qs.set('supplier', s);
  const r = await fetch(`${BASE}/purchase-orders/lookup/items?${qs.toString()}`, {
    credentials: 'include',
  });
  return unwrap<{ supplierFiltered: boolean; items: PoLookupItem[] }>(r);
}

// ── 발주처(매입처)별 결제조건 (2026-07-29, v2.023) ──────────────
/** 발주서 결제조건 기본값 — 등록된 매입처가 아니면 이 값을 쓴다. */
export const DEFAULT_PAYMENT_TERM = '익월말 현금결제';

export interface PoPaymentTermRow {
  id: number;
  supplierName: string;
  paymentTerm: string;
  updatedBy: string | null;
  updatedAt: string;
}

export async function listPoPaymentTerms(): Promise<PoPaymentTermRow[]> {
  const r = await fetch(`${BASE}/purchase-orders/payment-terms`, { credentials: 'include' });
  const data = await unwrap<{ items: PoPaymentTermRow[] }>(r);
  return data.items;
}

/**
 * 매입처 이름으로 등록된 결제조건 찾기 (발주서 작성 자동 입력).
 *   표기 차이('(주)'·띄어쓰기 등)는 서버의 norm_company 가 흡수한다.
 *   등록이 없으면 paymentTerm = null.
 */
export async function lookupPoPaymentTerm(
  supplier: string,
): Promise<{ paymentTerm: string | null; matchedSupplier: string | null }> {
  const qs = new URLSearchParams({ supplier });
  const r = await fetch(`${BASE}/purchase-orders/payment-terms/lookup?${qs.toString()}`, {
    credentials: 'include',
  });
  return unwrap<{ paymentTerm: string | null; matchedSupplier: string | null }>(r);
}

export async function savePoPaymentTerm(supplierName: string, paymentTerm: string): Promise<number> {
  const r = await fetch(`${BASE}/purchase-orders/payment-terms`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ supplierName, paymentTerm }),
  });
  const data = await unwrap<{ id: number }>(r);
  return data.id;
}

export async function deletePoPaymentTerm(id: number): Promise<void> {
  const r = await fetch(`${BASE}/purchase-orders/payment-terms/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await unwrap<{ id: number }>(r);
}

export async function listPos(): Promise<PoListRow[]> {
  const r = await fetch(`${BASE}/purchase-orders`, { credentials: 'include' });
  const data = await unwrap<{ orders: PoListRow[] }>(r);
  return data.orders;
}

export async function getPo(id: number): Promise<PoDetail> {
  const r = await fetch(`${BASE}/purchase-orders/${id}`, { credentials: 'include' });
  return unwrap<PoDetail>(r);
}

export async function createPo(input: PoCreateInput): Promise<PoCreateResult> {
  const r = await fetch(`${BASE}/purchase-orders`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return unwrap<PoCreateResult>(r);
}

export async function updatePo(id: number, input: PoUpdateInput): Promise<void> {
  const r = await fetch(`${BASE}/purchase-orders/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  await unwrap<{ id: number }>(r);
}

export async function deletePo(id: number): Promise<void> {
  const r = await fetch(`${BASE}/purchase-orders/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await unwrap<{ id: number }>(r);
}

export function poPdfDownloadUrl(id: number): string {
  return `${BASE}/purchase-orders/${id}/download.pdf`;
}

export interface PoLinkPurchaseResult {
  purchaseIds: number[];
  itemCount: number;
  /** 기존 품명·품목에 매칭된 건수 */
  matchedCount: number;
  /** 신규 품목으로 등록된 건수 */
  newItemCount: number;
  linkedSalesId: string | null;
  /** 만든 매입 줄 수(나눠 연동하면 품목 수보다 많다 · v2.284.0) */
  purchaseCount?: number;
  /** 영업건별 줄 수·수량(null = 재고 보유) */
  bySales?: { salesId: string | null; rows: number; qty: number }[];
}

/** 품목 하나를 영업건별로 몇 개씩(v2.284.0) — salesId null = 재고 보유 */
export interface PoLinkAllocation {
  sortOrder: number;
  parts: { salesId: string | null; qty: number }[];
}

/** 발주서 품목(sort_order) → 기존 item_master 코드 매칭 */
export interface PoLinkMatch {
  sortOrder: number;
  itemCode: string;
}

/**
 * 발주서 → 매입 N건 자동 등록.
 *   matches: 발주서 품목을 기존 품명·품목(item_master)에 매칭한 목록.
 *            매칭된 품목은 그 item_code 로 연결되어 신규 품목이 생기지 않는다.
 *            빠진 품목은 신규 AUTO 품목으로 등록된다.
 * (매입 발주서 PDF 는 영업건에 첨부하지 않음 — 영업 발주서=고객 발주서 전용)
 */
export async function linkPoToPurchases(
  poId: number,
  matches: PoLinkMatch[] = [],
  allocations: PoLinkAllocation[] = [],
  force = false,
): Promise<PoLinkPurchaseResult> {
  const r = await fetch(`${BASE}/purchase-orders/${poId}/link-purchase`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ matches, allocations, force }),
  });
  return unwrap<PoLinkPurchaseResult>(r);
}

export function computePoTotals(items: Pick<PoItemInput, 'qty' | 'unitPrice'>[]): {
  totalSupply: number;
  totalWithVat: number;
  lineTotals: number[];
} {
  let totalSupply = 0;
  const lineTotals: number[] = [];
  for (const it of items) {
    const lt = Math.round((Number(it.qty) || 0) * (Number(it.unitPrice) || 0));
    lineTotals.push(lt);
    totalSupply += lt;
  }
  return { totalSupply, totalWithVat: Math.round(totalSupply * 1.1), lineTotals };
}
